import {
  Box,
  Stack,
  Table,
  Typography,
  CircularProgress,
  Menu,
  MenuItem,
  useTheme,
} from "@mui/material";
import {
  VrsConfirmationModals,
  VrsIconButton,
  IconName,
  VrsErrorMsg,
  dateFormats,
} from "@veris-health/web-core";
import dayjs from "dayjs";
import React, { useRef, useState } from "react";
import {
  ArticleCategoryGroup,
  GetEducationalArticlePreview,
} from "@veris-health/virtual-doc-ms/lib/v1/api";
import { useAppSelector } from "../../../hooks/useAppSelector";
import {
  StyledTableCell,
  StyledTableBody,
  StyledTableRow,
} from "../../../ui/components/Tables/shared/StyledTableElements";
import {
  Column,
  TableColumnNames,
  VrsSortedTableHeader,
} from "../../../ui/components/VrsSortedTableHeader";
import { selectArticles, selectArticlesStatus } from "../articlesSlice";
import { categoryLabels } from "../containers/ArticleEditorContainer";

const columns: Column[] = [
  {
    id: TableColumnNames.Title,
    label: TableColumnNames.Title,
  },
  {
    id: TableColumnNames.Description,
    label: TableColumnNames.Description,
  },
  {
    id: TableColumnNames.Category,
    label: TableColumnNames.Category,
  },
  {
    id: TableColumnNames.ReadTime,
    label: TableColumnNames.ReadTime,
  },
  {
    id: TableColumnNames.DateUploaded,
    label: TableColumnNames.DateUploaded,
  },
  {
    id: TableColumnNames.Actions,
    label: TableColumnNames.Actions,
  },
];

const ActionsMenu = ({
  title,
  onEdit,
  onDelete,
}: {
  title: string;
  onDelete: () => void;
  onEdit: () => void;
}) => {
  const theme = useTheme();
  const anchorRef = useRef(null);
  const [open, setOpen] = useState<boolean>(false);
  const [confimationModalOpen, setConfirmationModalOpen] = useState<boolean>(false);

  return (
    <Stack direction="row" ref={anchorRef}>
      {confimationModalOpen && (
        <VrsConfirmationModals
          isOpen={confimationModalOpen}
          handleClose={() => setConfirmationModalOpen(false)}
          dialogHeader="Warning!"
          dialogContent={`You will delete "${title}" article.`}
          dialogSecondContentLine="Please confirm."
          onCancel={() => setConfirmationModalOpen(false)}
          onConfirm={() => {
            setConfirmationModalOpen(false);
            onDelete();
          }}
          cancelButtonText="Cancel"
          confirmButtonText="Continue"
        />
      )}

      <VrsIconButton
        iconProps={{ name: open ? IconName.ActiveDots : IconName.Dots }}
        onClick={() => setOpen(true)}
      />
      <Menu anchorEl={anchorRef.current} open={open} onClose={() => setOpen(false)}>
        <MenuItem
          onClick={() => {
            setConfirmationModalOpen(true);
            setOpen(false);
          }}
        >
          <Typography variant="body" color={theme.veris.colors.neutrals["grey-4"]}>
            Delete
          </Typography>
        </MenuItem>
        <MenuItem
          onClick={() => {
            setOpen(false);
            onEdit();
          }}
        >
          <Typography variant="body" color={theme.veris.colors.neutrals["grey-4"]}>
            Edit
          </Typography>
        </MenuItem>
      </Menu>
    </Stack>
  );
};

const StyledTableColumn = ({
  column,
  article,
  onEdit,
  onDelete,
}: {
  column: Column;
  article: GetEducationalArticlePreview;
  onDelete: () => void;
  onEdit: () => void;
}) => {
  const theme = useTheme();
  return (
    <StyledTableCell key={column.id}>
      {column.id === TableColumnNames.Title && (
        <Typography variant="subtitle1" color={theme.veris.colors.neutrals["grey-4"]}>
          {article.title || "N/A"}
        </Typography>
      )}
      {column.id === TableColumnNames.Description && (
        <Typography
          variant="subtitle2"
          color={theme.veris.colors.neutrals["grey-4"]}
          sx={{ wordBreak: "break-word", maxWidth: "350px" }}
        >
          {article.preview || "N/A"}
        </Typography>
      )}
      {column.id === TableColumnNames.Category && (
        <Typography variant="subtitle1" color={theme.veris.colors.neutrals["grey-4"]}>
          {article.category ? categoryLabels[article.category as ArticleCategoryGroup] : "N/A"}
        </Typography>
      )}
      {column.id === TableColumnNames.ReadTime && (
        <Typography variant="subtitle2" color={theme.veris.colors.neutrals["grey-4"]}>
          {article.read_time_minutes || "N/A"}
        </Typography>
      )}
      {column.id === TableColumnNames.DateUploaded && (
        <Typography variant="subtitle2" color={theme.veris.colors.neutrals["grey-4"]}>
          {article.date_uploaded
            ? dayjs(article.date_uploaded).format(dateFormats["MMM DD, YYYY"])
            : "N/A"}
        </Typography>
      )}

      {column.id === TableColumnNames.Actions && (
        <ActionsMenu title={article.title} onEdit={onEdit} onDelete={onDelete} />
      )}
    </StyledTableCell>
  );
};

export const ArticlesTable = ({
  fetchEducationalArticles,
  onEdit,
  onDelete,
}: {
  fetchEducationalArticles: () => void;
  onEdit: (article: GetEducationalArticlePreview) => void;
  onDelete: (article: GetEducationalArticlePreview) => void;
}): JSX.Element => {
  const articles = useAppSelector(selectArticles);
  const status = useAppSelector(selectArticlesStatus);

  return (
    <>
      {status === "loading" && (
        <Box height="50%" display="flex" alignItems="center" justifyContent="center">
          <CircularProgress />
        </Box>
      )}
      {status === "failed" && <VrsErrorMsg size="small" onClick={fetchEducationalArticles} />}
      {status === "idle" && (
        <Box
          sx={{
            backgroundColor: (theme) => theme.veris.colors.neutrals.white,
            border: (theme) => `1px solid ${theme.veris.colors.neutrals["grey-light"]}`,
            borderRadius: (theme) => theme.shape.borderRadius,
            height: "73vh",
            overflow: "auto",
          }}
          px={2}
          pb={1}
        >
          {articles?.items?.length > 0 ? (
            <Table>
              <VrsSortedTableHeader columns={columns} />
              <StyledTableBody>
                {articles.items.map((article: GetEducationalArticlePreview) => (
                  <StyledTableRow key={article.id}>
                    {columns.map((column) => (
                      <StyledTableColumn
                        column={column}
                        article={article}
                        key={column.id}
                        onEdit={() => onEdit(article)}
                        onDelete={() => onDelete(article)}
                      />
                    ))}
                  </StyledTableRow>
                ))}
              </StyledTableBody>
            </Table>
          ) : (
            <Typography
              color={(theme) => theme.veris.colors.neutrals["grey-3"]}
              textAlign="center"
              variant="body"
              mt={2}
            >
              There are no articles at this moment.
            </Typography>
          )}
        </Box>
      )}
    </>
  );
};
